import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { isUndefined } from '../../utils/utils'
import type { FieldValidationError } from '../../Types/ValidationError'
import { validate } from 'devx-js-utilities'
import { parseRelativeDatetime } from '../../DateTimeFunctions'
import moment from '../../Moment'
import { isEmpty } from 'ramda'
import I18n from 'i18n-react'

interface Props {
  error: FieldValidationError;
}

export default function FieldError({ error, name, templateVariables, prefix, onHideError }: Props) {
  const [showError, setShowError] = useState(!!error)

  useEffect(() => {
    setShowError(!!error)
  }, [error])

  const handleHideError = useCallback(() => {
    if (typeof onHideError === 'function') {
      onHideError()
    }
    setShowError(false)
  })
  const errorMessage = useMemo(() => getErrorText(error, name, templateVariables, prefix), [error])

  return !isEmpty(error) && showError ? (
    <div className="invalid-tooltip mt-0 cursor-pointer" onClick={handleHideError}>
      <span>{errorMessage}</span>
    </div>
  ) : null
}

function getMessageFromValidationError(validationError: FieldValidationError, prefix: string = '') {
  if (validationError) {
    let failing = validationError.failing ? `.${validationError.failing[0]}` : ''

    if (validationError.error_type !== 'string_pattern_error') {
      failing = typeof validationError.expected === 'string' ? `.${validationError.expected}` : failing
    }

    const prefixArray = prefix.split('.').filter((p) => !validate.isNumeric(p))

    const expected =
      typeof validationError.expected === 'string' ? { expected: validationError.expected } : validationError.expected

    const data = {
      minDate:
        expected &&
        !isUndefined(expected.minRelative) &&
        parseRelativeDatetime(expected.minRelative).format('DD.MM.YYYY'),
      maxDate:
        expected &&
        !isUndefined(expected.maxRelative) &&
        parseRelativeDatetime(expected.maxRelative).format('DD.MM.YYYY'),
    }

    const errorTypeToMessage =
      validationError.error_type !== 'error_triggered_by_effect'
        ? validationError.error_type
        : validationError.description

    return {
      msg: `${prefixArray.join('.')}${errorTypeToMessage}${failing}`,
      data: {
        ...expected,
        ...data,
        now: moment().format('DD.MM.YYYY'),
      },
    }
  }
}

function getErrorText(error, name, templateVariables, prefix = '') {
  const errorMessage = getMessageFromValidationError(error, `${prefix}${name}.`.replace(new RegExp('-', 'g'), '.'))

  if (!errorMessage) {
    return ''
  }
  return `${I18n.translate(`validationErrors.${errorMessage.msg}`, { ...templateVariables, ...errorMessage.data })}`
}
